import { translate } from "../internationalization";
import { NODE_FUNCTIONS_ENDPOINT } from "../config";
import supportedLanguages from "./supportedLanguages";

import { menuAutoTranslateBtnEl, bodyEl } from "../elements";
import store from "../store";
import { throwErrorWithStack } from "../crashlytics";
import { JobExecutor } from "../utils";
import _ from "lodash";
import { getLogger } from "../logging";

const log = getLogger("🌍 Translate");

// function translateEls() {
//   if (!TRANSLATE_ENABLED) return;
//   [
//     colorScoreEl,
//     menuChangeColorBtnEl,
//     menuUpgradeBtnEl,
//     menuLevelBtnEl,
//     // menuShareBtnEl,
//     // menuOpenLeaderboardBtnEl,
//     // levelIndicatorEl,
//     subscribeTitleEl,
//     subscribeYearlyEl,
//     subscribeMonthlyEl,
//     subscribeYearlyMonthEl,
//     subscribeMonthlyMonthEl,
//     subscribePopularEl,
//     subscribeSelectPlanEl,
//     subscribeUnlockLevelsDescriptionEl,
//     subscribeUnlockColorsDescriptionEl,
//     subscribeRmAdsDescriptionEl,
//     subscribeNowBtnEl,
//     introTextEl,
//     menuAchievementsBtnEl,
//   ].forEach((el, i) => {
//     if (el == null) log.error(`null element encountered at iteration ${i}`);
//     else el.innerHTML = translate(el.innerHTML);
//   });
// }

export function setTranslation() {
  menuAutoTranslateBtnEl.innerHTML = translate(
    "🌍 Translation: " + (store.disableTranslate ? "Disabled" : "Enabled")
  );
  resetLangAttributeOfLeafs(menuAutoTranslateBtnEl);
}

const translationJobExecutor = new JobExecutor();

export function getAllTranslations() {
  translationJobExecutor.addJob(jobGetAllTranslations);
}

async function jobGetAllTranslations() {
  if (store.languageCode === "en") return;
  try {
    const url =
      NODE_FUNCTIONS_ENDPOINT.replace("{function-name}", "translateBatch") +
      `getAllTranslations?languageCode=${store.languageCode}`;

    const response = await fetch(url);
    if (!response.ok)
      throw new Error(
        `Failed to get all translations: ${response.status} ${response.statusText}`
      );
    const translations = await response.json();
    store.translationCache[store.languageCode] = {
      ...store.translationCache[store.languageCode],
      ...translations,
    };
    store.save("translationCache");
    log.debug(
      `Loaded ${Object.keys(translations).length} ${
        store.languageCode
      } translations`
    );
  } catch (error) {
    throwErrorWithStack(error);
  }
}

export function autoTranslate() {
  translationJobExecutor.addJob(jobAutoTranslate);
}

async function jobAutoTranslate() {
  try {
    if (!shouldEnableTranslationFeature()) {
      log.debug(`Not translating ${store.languageCode}`);
      menuAutoTranslateBtnEl.style.display = "none";
      return;
    }
    if (store.disableTranslate) resetAllTranslations();
    else await translateAllElements();
  } catch (error) {
    throwErrorWithStack(error, false);
  }
}

export function shouldEnableTranslationFeature() {
  return store.languageCode !== "en" && supportedLanguages[store.languageCode];
}

export async function autoTranslateElements(...els) {
  resetLangAttributeOfLeafs(...els);
  translationJobExecutor.addJob(jobAutoTranslateElements, ...els);
}

async function jobAutoTranslateElements(...els) {
  if (store.languageCode === "en") return;
  let leafNodes = _.flatten(els.map(getLeafNodes));
  leafNodes = leafNodes.filter(
    (node) =>
      getAcceptNode(!store.disableTranslate)(node) === NodeFilter.FILTER_ACCEPT
  );
  if (store.disableTranslate) resetNodeTranslation(leafNodes);
  else {
    log.debug("Translating elements...");
    await translateNodes(leafNodes);
    log.debug("Translated");
  }
}

function resetNodeTranslation(nodes) {
  for (const node of nodes) {
    if (node.textContentOld) {
      node.textContent = node.textContentOld;
      resetLangAttribute(node);
      delete node.textContentOld;
    }
  }
}

function getLeafNodes(node) {
  let leaves = [];

  function findLeaves(currentNode) {
    if (currentNode.childNodes.length === 0) leaves.push(currentNode);
    else currentNode.childNodes.forEach(findLeaves);
  }
  findLeaves(node);

  return leaves;
}

async function translateAllElements() {
  log.debug("Translating all elements...");
  const textNodes = getAllElementsThatContainText();
  await translateNodes(textNodes);
  log.debug("Translated");
  document.documentElement.lang = store.languageCode;
  if (store.languageCode == "en" || store.disableTranslate)
    bodyEl.style.hyphens = "none";
  else bodyEl.style.hyphens = "auto";
}

function resetAllTranslations() {
  log.debug(`Resetting all translations`);
  const textNodes = getAllElementsThatContainText(false);
  resetNodeTranslation(textNodes);
  document.documentElement.lang = "en";
  bodyEl.style.hyphens = "none";
}

async function translateNodes(nodes) {
  if (!store.translationCache[store.languageCode]) {
    store.translationCache[store.languageCode] = {};
    store.save("translationCache");
  }
  const textNodes = nodes.filter((node) => node.textContent.trim().length);
  const texts = textNodes.map((node) => node.textContent);
  applyTranslations(textNodes, store.translationCache[store.languageCode]);
  let googleTranslatedTexts = [];
  const uncachedTexts = texts.filter(
    (text) => !store.translationCache[store.languageCode][text]
  );
  if (uncachedTexts.length) {
    log.debug(`Requesting translation of ${uncachedTexts.length} texts`);
    if (uncachedTexts.length == 1)
      googleTranslatedTexts = [
        await googleTranslate(uncachedTexts[0], {
          from: "en",
          to: store.languageCode,
        }),
      ];
    else
      googleTranslatedTexts = await googleTranslateBatch(uncachedTexts, {
        from: "en",
        to: store.languageCode,
      });
  }
  const googleTranslations = _.zipObject(uncachedTexts, googleTranslatedTexts);
  applyTranslations(textNodes, googleTranslations, __DEV__);
  store.save("translationCache");
}

function applyTranslations(nodes, translations, warnOnNotFound = false) {
  for (const node of nodes) {
    const text = node.textContent;
    const translatedText = translations[text];
    if (!translatedText) {
      if (warnOnNotFound && translatedText === null)
        log.warn(`Translation not found for: ${text}`);
      continue;
    }
    node.textContentOld = text;
    node.textContent = translatedText;
    store.translationCache[store.languageCode][text] = translatedText;
    setLangAttribute(node);
  }
}

function setLangAttribute(node) {
  node.lang = store.languageCode;
}

function resetLangAttribute(node) {
  delete node.lang;
}

export function resetLangAttributeOfLeafs(node) {
  const leafs = getLeafNodes(node);
  for (const leaf of leafs) delete leaf.lang;
}

function getLangAttribute(node) {
  return node.lang;
}

function getAllElementsThatContainText(langAttrCheck = true) {
  const textNodes = [];
  const walker = document.createTreeWalker(
    document.body,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode: getAcceptNode(langAttrCheck),
    },
    false
  );
  while (walker.nextNode()) textNodes.push(walker.currentNode);
  return textNodes;
}

const getAcceptNode = (langAttrCheck) =>
  function (node) {
    if (langAttrCheck && getLangAttribute(node) === store.languageCode)
      return NodeFilter.FILTER_REJECT;
    if (["SCRIPT", "style"].includes(node.parentElement.nodeName))
      return NodeFilter.FILTER_REJECT;
    if (node.nodeValue.trim().length === 0) return NodeFilter.FILTER_REJECT;
    let currentNode = node;
    while (currentNode && currentNode !== document.body) {
      if (
        currentNode.nodeType === Node.ELEMENT_NODE &&
        (currentNode.classList.contains("notranslate") ||
          currentNode.id === "wps-progress")
      )
        return NodeFilter.FILTER_REJECT;
      currentNode = currentNode.parentNode;
    }
    return NodeFilter.FILTER_ACCEPT;
  };

async function googleTranslateBatch(texts, { from, to }) {
  const url = NODE_FUNCTIONS_ENDPOINT + "translateBatch";
  const body = {
    texts,
    from,
    to,
  };
  const response = await fetch(url, {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!response.ok)
    throw new Error(
      `Failed to translate batch: ${response.status} ${response.statusText}`
    );
  return await response.json();
}

async function googleTranslate(text, { from, to }) {
  const urlEncodedText = encodeURIComponent(text);
  const url =
    NODE_FUNCTIONS_ENDPOINT +
    `translate?text=${urlEncodedText}&from=${from}&to=${to}`;
  const response = await fetch(url);
  if (!response.ok)
    throw new Error(
      `Failed to translate: ${response.status} ${response.statusText}`
    );
  return await response.text();
}
